import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ThumbsUp,
  Heart,
  Lightbulb,
  Brain,
  Flame,
  Share2,
  Copy,
  Check,
  MessageCircle,
  Send,
  Trash2,
  Facebook,
  Twitter,
  Linkedin,
} from "lucide-react";
import {
  getRadioSocial,
  getMyRadioReaction,
  getMyRadioCommentIds,
  setMyRadioReaction,
  addRadioComment,
  deleteMyRadioComment,
  type RadioReaction,
} from "@/lib/api/radio-social.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const REACTIONS: Array<{
  key: RadioReaction;
  label: string;
  Icon: typeof ThumbsUp;
  tone: string;
}> = [
  { key: "like", label: "Like", Icon: ThumbsUp, tone: "text-sky-600" },
  { key: "love", label: "Love", Icon: Heart, tone: "text-rose-600" },
  { key: "insightful", label: "Insightful", Icon: Lightbulb, tone: "text-amber-600" },
  { key: "thinking", label: "Hmm", Icon: Brain, tone: "text-indigo-600" },
  { key: "fire", label: "Fire", Icon: Flame, tone: "text-orange-600" },
];

export function RadioSocial({
  itemId,
  itemKind,
  shareText,
  shareUrl,
  compact = false,
}: {
  itemId: string;
  itemKind: "episode" | "track";
  shareText: string;
  shareUrl: string;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState("");
  const [openComments, setOpenComments] = useState(!compact);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const fetchSocial = useServerFn(getRadioSocial);
  const fetchMine = useServerFn(getMyRadioReaction);
  const mutateReaction = useServerFn(setMyRadioReaction);
  const sendComment = useServerFn(addRadioComment);
  const removeComment = useServerFn(deleteMyRadioComment);

  const socialQ = useQuery({
    queryKey: ["radio-social", itemId],
    queryFn: () => fetchSocial({ data: { itemId, itemKind } }),
    staleTime: 30_000,
  });

  const fetchMineIds = useServerFn(getMyRadioCommentIds);
  const mineQ = useQuery({
    queryKey: ["radio-mine", itemId, userId],
    queryFn: () => fetchMine({ data: { itemId, itemKind } }),
    enabled: !!userId,
  });
  const myCommentsQ = useQuery({
    queryKey: ["radio-mine-comments", itemId, userId],
    queryFn: () => fetchMineIds({ data: { itemId, itemKind } }),
    enabled: !!userId,
  });
  const myCommentIds = new Set(myCommentsQ.data?.ids ?? []);

  useEffect(() => {
    const channel = supabase
      .channel(`radio-social-${itemId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "radio_reactions", filter: `item_id=eq.${itemId}` },
        () => qc.invalidateQueries({ queryKey: ["radio-social", itemId] }),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "radio_comments", filter: `item_id=eq.${itemId}` },
        () => qc.invalidateQueries({ queryKey: ["radio-social", itemId] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [itemId, qc]);

  const reactMut = useMutation({
    mutationFn: (next: RadioReaction | null) =>
      mutateReaction({ data: { itemId, itemKind, reaction: next } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["radio-social", itemId] });
      qc.invalidateQueries({ queryKey: ["radio-mine", itemId, userId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save your reaction"),
  });

  const commentMut = useMutation({
    mutationFn: (body: string) => sendComment({ data: { itemId, itemKind, body } }),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["radio-social", itemId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not post comment"),
  });

  const deleteMut = useMutation({
    mutationFn: (commentId: string) => removeComment({ data: { commentId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["radio-social", itemId] }),
    onError: (e: any) => toast.error(e?.message ?? "Could not delete comment"),
  });

  const counts = socialQ.data?.counts;
  const total = socialQ.data?.total ?? 0;
  const comments = socialQ.data?.comments ?? [];
  const mine = mineQ.data?.reaction ?? null;

  function clickReaction(k: RadioReaction) {
    if (!userId) {
      toast.message("Sign in to react", {
        description: "Create a free account to react and join the conversation.",
      });
      return;
    }
    reactMut.mutate(mine === k ? null : k);
  }

  const encShare = encodeURIComponent(shareUrl);
  const encText = encodeURIComponent(shareText);
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encShare}`;
  const xHref = `https://twitter.com/intent/tweet?url=${encShare}&text=${encText}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encShare}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;

  async function handleShare() {
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({
          title: "NDL Justice Radio",
          text: shareText,
          url: shareUrl,
        });
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="mt-4 space-y-3 border-t border-border pt-3">
      <div className="flex flex-wrap items-center gap-2">
        {REACTIONS.map(({ key, label, Icon, tone }) => {
          const active = mine === key;
          const count = counts?.[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => clickReaction(key)}
              aria-pressed={active}
              aria-label={`${label} (${count})`}
              disabled={reactMut.isPending}
              className={
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition " +
                (active
                  ? "border-navy bg-navy text-navy-foreground"
                  : "border-input bg-background text-foreground/80 hover:border-navy")
              }
            >
              <Icon className={`h-3.5 w-3.5 ${active ? "" : tone}`} />
              <span>{label}</span>
              {count > 0 && (
                <span
                  className={
                    "rounded-full px-1.5 text-[10px] " +
                    (active ? "bg-navy-foreground/20" : "bg-muted text-muted-foreground")
                  }
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-muted-foreground">
          {total} {total === 1 ? "reaction" : "reactions"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpenComments((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium text-navy hover:border-navy"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {comments.length} {comments.length === 1 ? "comment" : "comments"}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium text-navy hover:border-navy"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Share2 className="h-3.5 w-3.5" />}
          Share
        </button>
        <a
          href={fbHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share to Facebook"
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-[#1877F2] hover:border-[#1877F2]"
        >
          <Facebook className="h-3.5 w-3.5" /> Facebook
        </a>
        <a
          href={xHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share to X / Twitter"
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-foreground/80 hover:border-navy"
        >
          <Twitter className="h-3.5 w-3.5" /> X
        </a>
        <a
          href={liHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share to LinkedIn"
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-[#0A66C2] hover:border-[#0A66C2]"
        >
          <Linkedin className="h-3.5 w-3.5" /> LinkedIn
        </a>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share to WhatsApp"
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-emerald-700 hover:border-emerald-600"
        >
          WhatsApp
        </a>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-navy hover:border-navy"
        >
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
      </div>

      {openComments && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
          {userId ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const body = draft.trim();
                if (body.length < 2) return;
                commentMut.mutate(body);
              }}
              className="flex gap-2"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={2000}
                placeholder="Add a comment…"
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <button
                type="submit"
                disabled={commentMut.isPending || draft.trim().length < 2}
                className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> Post
              </button>
            </form>
          ) : (
            <p className="text-xs text-muted-foreground">
              <Link to="/auth" className="font-medium text-navy hover:underline">
                Sign in
              </Link>{" "}
              to join the conversation.
            </p>
          )}

          {comments.length === 0 ? (
            <p className="text-xs text-muted-foreground">Be the first to comment.</p>
          ) : (
            <ul className="space-y-2">
              {comments.map((c) => (
                <li key={c.id} className="rounded-md border border-border bg-background p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-navy">{c.author_name || "Listener"}</p>
                    <div className="flex items-center gap-2">
                      <time className="text-[10px] text-muted-foreground">
                        {new Date(c.created_at).toLocaleString("en-ZA")}
                      </time>
                      {myCommentIds.has(c.id) && (
                        <button
                          type="button"
                          onClick={() => deleteMut.mutate(c.id)}
                          aria-label="Delete comment"
                          className="text-muted-foreground hover:text-rose-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{c.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
